import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "../ui/button";
import { AutoSaveNote } from "./AutoSaveNote";
import {
  CheckCircle2,
  Circle,
  Clock,
  Trash2,
  StickyNote,
  GripVertical,
  MessageCircle,
} from "lucide-react";

export function SortableStopItem({ 
  postcode, 
  index, 
  onDelete, 
  onEditNotes, 
  onToggleComplete,
  editingNotes,
  onAutoSaveNotes,
  eta, 
  formatTime 
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: postcode.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : "auto",
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`p-4 rounded-xl border mb-3 transition-all ${
        postcode.completed
          ? "bg-green-50 border-green-200"
          : "bg-white border-slate-200 hover:shadow-md"
      }`}
      data-testid={`stop-item-${postcode.id}`}
    >
      <div className="flex items-center gap-3">
        {/* Drag Handle */}
        <button
          className="touch-none cursor-grab active:cursor-grabbing p-1"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="w-5 h-5 text-slate-300" />
        </button>

        {/* Index number */}
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0 ${
          postcode.completed ? "bg-green-500" : "bg-slate-900"
        }`}>
          {index + 1}
        </div>

        {/* Main content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className={`font-semibold truncate ${
              postcode.completed ? "text-green-800 line-through" : "text-slate-900"
            }`}>
              {postcode.name}
            </h3>
            {postcode.completed && (
              <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
            )}
          </div>
          <p className="text-sm text-slate-500">{postcode.postcode}</p>
          
          {/* ETA badge */}
          {!postcode.completed && eta && (
            <div className="flex items-center gap-1 mt-1">
              <Clock className="w-3 h-3 text-blue-500" />
              <span className="text-xs text-blue-600 font-medium">
                ETA: {formatTime(eta.cumulative_duration)}
                <span className="text-blue-400 ml-1">
                  ({(eta.cumulative_distance / 1000).toFixed(1)} km)
                </span>
              </span>
            </div>
          )}

          {/* WhatsApp share */}
          {!postcode.completed && eta && (
            <a
              href={`https://wa.me/?text=${encodeURIComponent(
                `Arriving at ${postcode.name} (${postcode.postcode}) in approx. ${formatTime(eta.cumulative_duration)} (${(eta.cumulative_distance / 1000).toFixed(1)} km away)`
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-green-600 hover:text-green-800 mt-1"
            >
              <MessageCircle className="w-3 h-3" />
              Share ETA
            </a>
          )}

          {/* Notes — auto-save inline editor or display */}
          {editingNotes === postcode.id ? (
            <div className="mt-2">
              <AutoSaveNote
                value={postcode.notes}
                onSave={(notes) => onAutoSaveNotes(postcode.id, notes)}
              />
            </div>
          ) : postcode.notes ? (
            <p
              className="text-xs text-slate-600 bg-amber-50 p-2 rounded border border-amber-100 mt-2 cursor-pointer hover:bg-amber-100 transition-colors"
              onClick={() => onEditNotes(postcode)}
            >
              <StickyNote className="w-3 h-3 inline mr-1" />
              {postcode.notes}
            </p>
          ) : null}
        </div>

        {/* Actions */}
        <div className="flex gap-1">
          {/* Complete / Undo toggle */}
          <Button
            variant="ghost"
            size="icon"
            data-testid={`toggle-complete-${postcode.id}`}
            onClick={() => onToggleComplete(postcode.id, postcode.completed)}
            className={postcode.completed 
              ? "text-green-500 hover:text-orange-500 hover:bg-orange-50" 
              : "text-slate-400 hover:text-green-600 hover:bg-green-50"}
            title={postcode.completed ? "Mark incomplete" : "Mark complete"}
          >
            {postcode.completed ? <CheckCircle2 className="w-5 h-5" /> : <Circle className="w-5 h-5" />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onEditNotes(postcode)}
            className={`text-slate-400 hover:text-amber-600 ${postcode.notes ? 'text-amber-500' : ''}`}
          >
            <StickyNote className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            data-testid={`delete-stop-${postcode.id}`}
            onClick={() => onDelete(postcode.id)}
            className="text-slate-400 hover:text-red-500"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
